import { z } from "zod";
import { useId } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { cn } from "@/shared/lib/utils";
import {
  ChartSkeleton,
  CHART_TOOLTIP,
  CHART_GRID,
  CHART_AXIS_TICK,
  CHART_COLORS,
} from "@/shared/components";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const TimelineChartSchema = z.object({
  data: z.array(
    z.object({
      month: z.string(),
      value: z.number(),
      label: z.string().optional(),
    }),
  ),
  metric: z.string(),
  color: z.string().optional(),
});

type TimelineChartData = z.infer<typeof TimelineChartSchema>;

/* ────────────────────── Component ────────────────────── */

export function TimelineChart({
  widget,
  className,
}: AgentWidgetProps<TimelineChartData>) {
  const { data, metric, color } = widget.data;
  const fillColor = color ?? CHART_COLORS[0];
  const reactId = useId();
  const gradientId = `timeline${reactId}`;

  return (
    <div className={cn("w-full", className)}>
      <p className="mb-3 text-[11px] font-medium text-text-tertiary">
        {metric}
      </p>

      <ResponsiveContainer width="100%" height={180}>
        <AreaChart
          data={data}
          margin={{ top: 4, right: 4, left: -16, bottom: 0 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={fillColor} stopOpacity={0.3} />
              <stop offset="95%" stopColor={fillColor} stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid {...CHART_GRID} vertical={false} />
          <XAxis
            dataKey="month"
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) =>
              v >= 1_000_000
                ? `${(v / 1_000_000).toFixed(1)}M`
                : v >= 1_000
                  ? `${(v / 1_000).toFixed(0)}K`
                  : String(v)
            }
          />
          <Tooltip
            {...CHART_TOOLTIP}
            formatter={(value: number) => [
              value.toLocaleString("ko-KR"),
              metric,
            ]}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={fillColor}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function TimelineChartSkeleton({ className }: { className?: string }) {
  return <ChartSkeleton height={200} className={className} />;
}

/* ────────────────────── Widget Definition ────────────────────── */

const timelineChartWidget = defineWidget<TimelineChartData>({
  type: WIDGET_TYPES.TIMELINE_CHART,
  label: "월별 통계 AreaChart",
  schema: TimelineChartSchema,
  component: TimelineChart,
  skeleton: TimelineChartSkeleton,
  defaultLayout: "full",
  maxHeight: 240,
  aiHint: "월별 지표 추이를 그라디언트 영역 차트로 시각화",
});

registerWidget(timelineChartWidget);
